import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useBranding } from '../core/branding/BrandingContext';
import { ConfigLoader } from '../core/config/ConfigLoader';
import { tenantResolver } from '../core/tenant/TenantResolver';
import { LoadingScreen } from '../components/feedback/LoadingScreen';
import { TenantName, AVAILABLE_TENANTS } from '../core/types/tenant.types';
import { useAuth } from '../components/auth/SimpleAuthProvider';

export const MapPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { branding, projectName } = useBranding();
  const { user, login } = useAuth();
  const [isTerriaLoaded, setIsTerriaLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAutoLoggingIn, setIsAutoLoggingIn] = useState(false);
  const [selectedOptionTitle, setSelectedOptionTitle] = useState<string | null>(null);

  // CHECK FOR AUTO-LOGIN PARAMS IMMEDIATELY (for combined dashboard)
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const emailParam = urlParams.get('email');
    const fromCombined = urlParams.get('from') === 'combined';

    if (emailParam && fromCombined && !user && !isAutoLoggingIn) {
      setIsAutoLoggingIn(true);
      login(emailParam).then(() => {
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete('email');
        cleanUrl.searchParams.delete('from');
        window.history.replaceState({}, '', cleanUrl.pathname);
        setIsAutoLoggingIn(false);
      }).catch(() => {
        setIsAutoLoggingIn(false);
      });
    }
  }, [location.search, user, login, isAutoLoggingIn]);

  useEffect(() => {
    if (isAutoLoggingIn) return;

    const loadTerria = async () => {
      try {
        const urlParams = new URLSearchParams(location.search);
        const appParam = urlParams.get('app');

        let targetTenant: TenantName = projectName as TenantName;

        if (appParam && AVAILABLE_TENANTS.includes(appParam as TenantName)) {
          tenantResolver.setTenant(appParam as TenantName);
          targetTenant = appParam as TenantName;
        }

        await ConfigLoader.loadTerriaConfig(targetTenant);

        // 🔥 Get selected catalog from sessionStorage
        const selectedCatalog = sessionStorage.getItem('selected_catalog') || 'catalog_all.json';
        const optionTitle = sessionStorage.getItem('selected_option_title');
        setSelectedOptionTitle(optionTitle);
        console.log('📌 Selected option title from sessionStorage:', optionTitle);

        let terriaUrl: URL;

        if (process.env.NODE_ENV === 'development') {
          terriaUrl = new URL('http://localhost:3001/');
        } else {
          // 🔥 FIXED: Use /terriamap/ instead of /api/terriamap/
          terriaUrl = new URL(`${window.location.origin}/terriamap/`);
        }

        terriaUrl.searchParams.set('project', targetTenant);
        terriaUrl.searchParams.set('catalog', selectedCatalog);

        const token = localStorage.getItem('auth_token');
        if (token) {
          terriaUrl.searchParams.set('token', token);
        }

        console.log('🗺️  Loading TerriaMap from:', terriaUrl.toString());
        console.log('📚 Selected catalog:', selectedCatalog);

        const iframe = document.getElementById('terria-iframe') as HTMLIFrameElement;
        if (iframe) {
          iframe.src = terriaUrl.toString();
          iframe.onload = () => {
            setIsTerriaLoaded(true);
          };
        }
      } catch (err) {
        console.error('Error loading TerriaMap:', err);
        setError('Failed to load map. Please try again.');
      }
    };

    loadTerria();
  }, [location.search, projectName, isAutoLoggingIn]);

  const handleBackToDashboard = () => {
    sessionStorage.removeItem('selected_catalog');
    sessionStorage.removeItem('selected_option_title');
    navigate('/dashboard');
  };

  if (isAutoLoggingIn) {
    return <LoadingScreen message="Authenticating..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="text-gray-700 mb-4">{error}</p>
          <button
            onClick={handleBackToDashboard}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!branding) {
    return <LoadingScreen />;
  }

  return (
    <div className="h-screen flex flex-col">
      <header
        className="flex items-center justify-between px-6 py-3 shadow-md z-10"
        style={{ backgroundColor: branding.primaryColor }}
      >
        <div className="flex items-center space-x-4">
          {branding.logoUrl && (
            <img
              src={branding.logoUrl}
              alt={`${projectName} logo`}
              className="h-8 object-contain"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          )}
          <h1 className="text-lg font-semibold text-white">
            {selectedOptionTitle ?? branding.headingText}
          </h1>
        </div>

        <button
          onClick={handleBackToDashboard}
          className="px-4 py-2 bg-white rounded-lg text-sm font-semibold hover:bg-gray-100 transition-colors"
          style={{ color: branding.primaryColor }}
        >
          Back to Dashboard
        </button>
      </header>

      <div className="flex-1 relative">
        {!isTerriaLoaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
            <LoadingScreen />
          </div>
        )}
        <iframe
          id="terria-iframe"
          title="TerriaMap"
          className="w-full h-full border-0"
          allow="geolocation; fullscreen"
        />
      </div>
    </div>
  );
};